import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi, AuthAPIError } from '@/lib/auth-api';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

interface ForgotPasswordFormProps {
	onBack: () => void;
}

export function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);
			setIsLoading(true);

			try {
				const response = await authApi.forgotPassword({ email });

				if (response.success) {
					setSuccess(true);
				} else {
					setError(response.message || t('Odoslanie zlyhalo'));
				}
			} catch (err) {
				if (err instanceof AuthAPIError) {
					setError(err.message);
				} else {
					setError(t('Nastala neočakávaná chyba'));
				}
			} finally {
				setIsLoading(false);
			}
		},
		[email, t]
	);

	// Success state
	if (success) {
		return (
			<div data-ui="forgot-password-success" className="space-y-4">
				<div
					data-ui="forgot-password-success-alert"
					role="alert"
					className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
				>
					<CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-600" />
					<span>
						{t(
							'Ak existuje účet s touto emailovou adresou, odoslali sme vám pokyny na obnovenie hesla. Skontrolujte svoju emailovú schránku.'
						)}
					</span>
				</div>

				<Button
					data-ui="forgot-password-back-btn"
					type="button"
					variant="outline"
					size="lg"
					className="w-full"
					onClick={onBack}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		);
	}

	return (
		<form data-ui="forgot-password-form" onSubmit={handleSubmit} className="space-y-4">
			{/* Description */}
			<p className="text-sm text-muted-foreground">
				{t('Zadajte svoju emailovú adresu a my vám pošleme odkaz na obnovenie hesla.')}
			</p>

			{/* Error Alert */}
			{error && (
				<div
					data-ui="forgot-password-error-alert"
					role="alert"
					className="flex items-center gap-2 rounded-lg border border-destructive/50 px-4 py-3 text-sm text-destructive"
				>
					<AlertCircle className="h-4 w-4 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			{/* Email */}
			<div className="space-y-2">
				<Label htmlFor="email">{t('Emailová adresa')}</Label>
				<Input
					id="email"
					data-ui="forgot-password-email-input"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder={t('vas@email.sk')}
					autoComplete="email"
					autoFocus
					disabled={isLoading}
					required
				/>
			</div>

			{/* Actions */}
			<div className="space-y-2">
				<Button
					data-ui="forgot-password-submit-btn"
					type="submit"
					size="lg"
					className="w-full"
					disabled={isLoading || !email}
				>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							{t('Odosielam...')}
						</>
					) : (
						t('Obnoviť heslo')
					)}
				</Button>

				<Button
					type="button"
					data-ui="forgot-password-back-to-login-btn"
					variant="ghost"
					size="lg"
					className="w-full"
					onClick={onBack}
					disabled={isLoading}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		</form>
	);
}
